import React from "react";
import ReactDOM from "react-dom";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { datadogLogs } from "@datadog/browser-logs";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PersistGate } from "redux-persist/integration/react";
import { searchParamsStorage } from "@analytics/parseSearchParams/searchParamsStorage";
import setupAppsFlyer from "@analytics/setupAppsFlyer";
import setupDatadog from "@analytics/setupDatadog/setupDatadog";
import setupGtm from "@analytics/setupGtm";
import {
  getGoogleClientId,
  getIndexedDBInitializationTimeout,
  getIsResilientToDomMutationsWorkaroundEnabled,
  loadEnvironment,
} from "environment";
import parseSearchParams from "src/analytics/parseSearchParams/parseSearchParams";
import {
  clearOldBufferEvents,
  dataMeshParamsStorage,
  initSessionStartEventRefresh,
  startNewSession,
} from "src/core/analytics/exports/utils";
import { DatadogController } from "src/core/logger/DatadogController";
import { startServiceWorker } from "src/core/pushNotification/setupServiceWorker";
import { persistedCacheStorage } from "src/core/storage/indexedDB/makeIndexedDBPersistConfig";
import { DataDogErrorMessage } from "src/enums";
import { AutoLoginGate } from "src/features/autoLogin/exports/components";
import { handleSendFirstLandingEvent } from "src/features/marketing/utils/handleSendFirstLandingEvent";
import {
  CountryAccessError,
  checkCountryAccess,
} from "src/features/ofac/exports/api";
import { pickLocale } from "src/loadTranslations";
import configureStore from "src/state/configureStore";
import App from "src/ui/App";
import acmeCheckLostMessage from "src/utils/acmeCheckLostMessage";
import { setInMemoryStore } from "src/utils/getStoreHandler";
import { initializeLocalStorage } from "src/utils/initializeLocalStorage";
import resilientToDomMutationsWorkaround from "src/utils/resilientToDomMutationsWorkaround";
import {
  detectDeviceGPUModel,
  detectDeviceType,
  setDeviceLocale,
} from "state/flows/deviceInfo";
import { deviceInfoSelectors } from "state/selectors";
import { BreakpointProvider } from "ui/Breakpoint";
import ErrorBoundary from "ui/ErrorBoundary";
import IntlProvider from "ui/IntlProvider";
import ErrorView from "ui/common/errorView/ErrorView";
import setupIntercom from "ui/intercom/setupIntercom";
import lazyWithPageReload from "utils/lazyWithPageReload";
import { raceWithTimeout } from "utils/raceWithTimeout";
import validateSessionDetails from "utils/validateSession";
import "generated/modernizr";
import "./index.scss";

const twcVersion = `${GENERATED_APP_INFO.fullVersion}`;
window.twcVersion = twcVersion;
console.log(`Tango Web Client v${twcVersion}`); // eslint-disable-line no-console
const locale = pickLocale();

initializeLocalStorage();

const loadEnvironmentPromise = loadEnvironment();

const RestrictedApp = lazyWithPageReload(() =>
  import(
    /* webpackChunkName: "RestrictedApp" */ "src/features/ofac/exports/components"
  ).then((module) => ({ default: module.RestrictedApp }))
);

loadEnvironmentPromise
  .then(async () => {
    const isProd = process.env.NODE_ENV !== "development";
    setupDatadog();

    if (isProd) {
      setupGtm();

      if (getIsResilientToDomMutationsWorkaroundEnabled()) {
        resilientToDomMutationsWorkaround();
      }
    }

    await validateSessionDetails();

    try {
      // You should increment the version if you change
      // the structure of the database or when you add new tables
      const VERSION = 4;

      await raceWithTimeout(
        async (signal) => {
          await Promise.allSettled([
            persistedCacheStorage.init(VERSION, signal),
            dataMeshParamsStorage.init(VERSION, signal),
            searchParamsStorage.init(VERSION, signal),
          ]);
        },
        getIndexedDBInitializationTimeout(),
        "Storage initialization timeout"
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);

      datadogLogs.logger.warn(
        DataDogErrorMessage.STORAGE_INIT_ERROR,
        {},
        error
      );
    }

    const { store, persistor } = configureStore();

    store.subscribe(() => {
      setInMemoryStore(store.getState());
    });

    initSessionStartEventRefresh();
    startNewSession();
    handleSendFirstLandingEvent();
    clearOldBufferEvents();

    await checkCountryAccess();

    acmeCheckLostMessage(store.dispatch, store.getState);
    parseSearchParams();

    store.dispatch(detectDeviceType());
    store.dispatch(setDeviceLocale(locale));
    store.dispatch(detectDeviceGPUModel());

    startServiceWorker(
      deviceInfoSelectors.getDeviceType(store.getState())
    ).catch((e) => {
      datadogLogs.logger.warn("ServiceWorker Failed to start", {}, e);
    });

    ReactDOM.render(
      <ErrorBoundary>
        <Provider store={store}>
          <DatadogController />
          <GoogleOAuthProvider clientId={getGoogleClientId()}>
            <BreakpointProvider>
              <IntlProvider>
                <PersistGate loading={null} persistor={persistor}>
                  <AutoLoginGate>
                    <App />
                  </AutoLoginGate>
                </PersistGate>
              </IntlProvider>
            </BreakpointProvider>
          </GoogleOAuthProvider>
        </Provider>
      </ErrorBoundary>,
      document.getElementById("root")
    );

    setupAppsFlyer();

    if (isProd) {
      setupIntercom();
    }
  })
  .catch((err) => {
    const { store } = configureStore();
    store.dispatch(detectDeviceType());
    store.dispatch(detectDeviceGPUModel());
    store.dispatch(setDeviceLocale(locale));

    if (err instanceof CountryAccessError) {
      ReactDOM.render(
        <Provider store={store}>
          <BreakpointProvider>
            <IntlProvider>
              <RestrictedApp />
            </IntlProvider>
          </BreakpointProvider>
        </Provider>,
        document.getElementById("root")
      );

      datadogLogs.logger.warn(
        DataDogErrorMessage.COUNTRY_ACCESS_ERROR,
        {},
        err
      );

      return;
    }

    console.error("Failed to start up the app", err); //eslint-disable-line

    datadogLogs.logger.warn(
      DataDogErrorMessage.FAILED_TO_START_UP_THE_APP,
      {},
      err
    );

    ReactDOM.render(
      <Provider store={store}>
        <BreakpointProvider>
          <ReactIntlProvider textComponent="span" locale={locale}>
            <ErrorView testId="ui-error" />
          </ReactIntlProvider>
        </BreakpointProvider>
      </Provider>,
      document.getElementById("root")
    );
  });
